import { t } from '@lingui/core/macro'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { ChevronDown } from 'lucide-react'

export enum SortBy {
  ALL_TYPES = 'All types',
  PENDING = 'Pending',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  GENERAL = 'General',
}

interface CommentsSelectProps {
  value?: SortBy
  onChange?: (value: SortBy) => void
}

const isSortByValue = (value: string): value is SortBy =>
  Object.values(SortBy).includes(value as SortBy)

const CustomIcon = () => (
  <div
    style={{
      position: 'absolute',
      left: '10px',
      pointerEvents: 'none',
    }}
  >
    <ChevronDown color="#64748B" size={16} strokeWidth={2} />
  </div>
)

export const CommentsSelect = ({ value, onChange }: CommentsSelectProps) => {
  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value
    if (isSortByValue(newValue)) {
      onChange?.(newValue)
    }
  }

  const displayNames = {
    [SortBy.ALL_TYPES]: t`All types`,
    [SortBy.PENDING]: t`Pending`,
    [SortBy.APPROVED]: t`Approved`,
    [SortBy.REJECTED]: t`Rejected`,
    [SortBy.GENERAL]: t`General`,
  }

  return (
    <FormControl
      sx={{
        minWidth: 115,
        '& .MuiSelect-select': {
          cursor: 'pointer',
        },
      }}
    >
      <Select
        value={value}
        onChange={handleChange}
        displayEmpty
        size="small"
        IconComponent={CustomIcon}
        sx={{
          height: '36px',
          color: '#64748B',
          cursor: 'pointer',
          '& .MuiSelect-icon': {
            pointerEvents: 'auto',
          },
          '.MuiSelect-select': {
            padding: '10px 15px 10px 35px !important',
            textAlign: 'right',
          },
          '.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#64748B !important',
          },
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#64748B !important',
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: '#CAD8DE !important',
          },
        }}
        inputProps={{
          'aria-label': 'Without label',
          style: { cursor: 'pointer' },
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        {Object.values(SortBy).map((name, index) => (
          <MenuItem
            key={name}
            value={name}
            data-testid={`option-${String(index)}`}
            sx={{ cursor: 'pointer' }}
          >
            {displayNames[name]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
